import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Badge,
    Box,
    Card,
    CardBody,
    Flex,
    Heading,
    SimpleGrid,
    Spinner,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import AuthContext from '../context/AuthContext';
import { buildAdviserTimeSummary, buildBookImpactCards, formatCount, formatEur } from '../utils/bookImpact';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

export function BookImpactView({ payload }) {
    const cards = buildBookImpactCards(payload);
    const adviserTime = buildAdviserTimeSummary(payload);
    const book = payload.book || {};

    return (
        <Card bg="white" borderRadius="lg" boxShadow="md" data-testid="book-impact">
            <CardBody>
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3} mb={4}>
                    <Box>
                        <Heading size="md" color="brand.navy">Book impact in euros</Heading>
                        <Text fontSize="sm" color="gray.600">
                            {formatEur(book.aum_eur)} across {formatCount(book.customers_with_valued_holdings)} clients with valued holdings
                            {Number.isFinite(book.customer_coverage_pct) ? ` (${book.customer_coverage_pct}% of the book)` : ''}.
                        </Text>
                    </Box>
                    {book.includes_demo_data ? (
                        <Badge colorScheme="orange" variant="subtle">Includes demo data</Badge>
                    ) : null}
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
                    {cards.map((card) => (
                        <Tooltip key={card.key} label={card.basis} hasArrow placement="top" isDisabled={!card.basis}>
                            <Box borderWidth="1px" borderColor="brand.divider" borderRadius="md" p={4} tabIndex={0}>
                                <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" color="gray.500">
                                    {card.audience}
                                </Text>
                                <Text fontSize="sm" color="gray.700" fontWeight="semibold" mt={1}>{card.title}</Text>
                                <Text
                                    fontSize={card.available ? '2xl' : 'md'}
                                    fontWeight="bold"
                                    color={card.available ? 'brand.navy' : 'gray.500'}
                                    mt={2}
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {card.value}
                                </Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>{card.detail}</Text>
                            </Box>
                        </Tooltip>
                    ))}
                </SimpleGrid>

                <Text fontSize="sm" color="gray.600" mt={4}>
                    {`Adviser time: ${adviserTime.text}`}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={2}>
                    {payload.disclaimer} Method {payload.methodology_version}.
                </Text>
            </CardBody>
        </Card>
    );
}

export default function BookImpactPanel() {
    const { token } = useContext(AuthContext);
    const [state, setState] = useState({ status: 'loading' });

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_BASE_URL}/content/product-data/book-impact`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => { if (!cancelled) setState({ status: 'ready', payload: response.data }); })
            .catch((error) => {
                if (!cancelled) setState({ status: 'error', httpStatus: error.response?.status });
            });
        return () => { cancelled = true; };
    }, [token]);

    if (state.status === 'loading') {
        return (
            <Flex align="center" gap={3} py={4}>
                <Spinner size="sm" color="brand.teal" />
                <Text fontSize="sm" color="gray.600">Loading book impact...</Text>
            </Flex>
        );
    }

    if (state.status === 'error') {
        const message = state.httpStatus === 403
            ? 'Book impact in euros is part of the Business plan.'
            : 'Book impact could not be loaded. Other sections below are unaffected.';
        return (
            <Card bg="white" borderRadius="lg" boxShadow="sm">
                <CardBody><Text fontSize="sm" color="gray.600">{message}</Text></CardBody>
            </Card>
        );
    }

    return <BookImpactView payload={state.payload} />;
}
